import { FC, Ref, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CustomPopup from "../../../components/CustomPopup/CustomPopup";
import {
  T_CustomerLobDetails,
  T_UserProfile,
} from "../../../globalTypes/GlobalTypes";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AlertRhombus from "../../../assets/icons/alert-rhombus.svg";
import dragHandle from "../../../assets/icons/Drag-Handle.svg";
import HealthSuccessGif from "../../../assets/gif/Success.json";
import "./EditProfilePopup.scss";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { enUS } from "@mui/x-date-pickers/locales";
import dayjs, { Dayjs } from "dayjs";
import { emailRegex, panRegex } from "../../../utils/constants";
import { getLogoutInfo, isValidateEmail } from "../../../utils/helper";
import { debounce } from "lodash";
import CommonGifAnimation from "../../../components/CustomGifAnimation/CommonGifAnimation";
import {
  useLazyLogoutApiQuery,
  useLazyUpdateUserProfileDetailsQuery,
} from "../GeneralAppElements_Api";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import CustomThreeDotsLoader from "../../../components/CustomThreeDotsLoader/CustomThreeDotsLoader";
import {
  eraseCookie,
  INTERNAL_AUTH_TOKEN,
} from "../../../utils/CSCookieHandler";
import { T_GenerateTokenRes } from "../../Feature_Login/Login_Types";
import { updateUserProfileDetails } from "../../../redux-store/reducers/CsAppSlice";

const usLocale =
  enUS.components.MuiLocalizationProvider.defaultProps.localeText;

interface Props {
  open: boolean;
  handleOpen: any;
  fieldName: string;
}

const fieldNameMapping: any = {
  pan: "PAN",
  dob: "DOB",
  emailId: "Email Id",
};

const EditProfilePopup: FC<Props> = ({ open, handleOpen, fieldName }) => {
  const userDetails: any = useAppSelector(
    (state) => state.CsAppSlice.userDetails
  );
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const [profileData, setProfileData] = useState<any>({
    dob: userDetails?.dob,
    emailId: userDetails?.emailId,
    pan: userDetails?.pan,
  });
  const [isApiSuccess, setIsApiSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [popupWidth, setPopupWidth] = useState<string>("auto");
  const [helperText, setHelperText] = useState<string>("");
  const dispatch = useAppDispatch();
  const [updateUserProfileDetailsApi] = useLazyUpdateUserProfileDetailsQuery();
  const [getLogOutApi] = useLazyLogoutApiQuery();

  useEffect(() => {
    const element: any = document.getElementsByClassName("hamburger-menu-page");
    if (element.length > 0) {
      const width = element[0].clientWidth - 32;
      setPopupWidth(width.toString());
    }
  }, []);

  const handleSave = async () => {
    let reqPayload = {
      csUserId: csAppSlice.csUserId,
      name: userDetails.name,
      pan: userDetails.pan,
      dob: userDetails.dob,
      mobile: userDetails.mobileNumber,
      emailId: userDetails.emailId,
      ...profileData,
    };
    setIsLoading(true);
    await updateUserProfileDetailsApi(reqPayload).then((res) => {
      if (res.isSuccess && res.data?.code === API_STATUS_CODE.SUCCESS) {
        setIsApiSuccess(true);
        let updatedData = { ...userDetails, ...profileData };
        dispatch(updateUserProfileDetails(updatedData));
      }
      setIsLoading(false);
    });
  };

  const handleOnchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (fieldName === "pan") {
      handlePanChangeWithDebounce(value);
    } else {
      setProfileData({
        ...profileData,
        [fieldName]: value,
      });
      //   if (!isValidateEmail(value)) {
      //     setHelperText("Email is not valid. Please enter a valid email");
      //   } else {
      setHelperText("");
      //   }
    }
  };

  const handlePanChangeWithDebounce = useCallback(
    debounce((value: string) => {
      const upperValue = value.toUpperCase();
      setProfileData({
        ...profileData,
        [fieldName]: upperValue,
      });
      if (upperValue.length === 10 && !panRegex.test(upperValue)) {
        setHelperText("Please enter a valid PAN.");
      } else {
        setHelperText("");
      }
    }, 100),
    []
  );

  const handleDOBchange = (value: Dayjs | null) => {
    if (value?.isAfter(dayjs()) || value === null) {
      setProfileData({
        ...profileData,
        [fieldName]: null,
      });
      return;
    }
    if (dayjs(value).isAfter("1900-01-01") && dayjs(value).isValid()) {
      setProfileData({
        ...profileData,
        [fieldName]: value.format("YYYY/MM/DD"),
      });
    }
    if (helperText) {
      setHelperText("");
    }
  };

  const logoutEvent = (event: any) => {
    event.preventDefault();
    setIsLoading(true);
    getLogOutApi({
      csUserId: "" + userDetails?.CS_USER_ID,
    }).then(async (res) => {
      const logoutExtraInfo = getLogoutInfo(
        csAppSlice?.tokenDetails as T_GenerateTokenRes,
        userDetails as T_UserProfile & T_CustomerLobDetails
      );
      eraseCookie(INTERNAL_AUTH_TOKEN);
      dispatch({
        type: "USER_LOGOUT",
        payload: { ...logoutExtraInfo, clearLocalStorage: true },
      });
    });
  };

  let isPanField = fieldName === "pan";

  return (
    <CustomPopup
      open={open}
      handleOpen={() => handleOpen("")}
      btnLabel={isApiSuccess ? "" : "Go Back"}
      className="edit-profile-popup"
      btnStartIcon={<ChevronLeftIcon fontSize="small" />}
    >
      {isApiSuccess ? (
        <Box padding=".75rem 1rem 2rem 1rem" width={popupWidth + "px"}>
          <Box display="flex" justifyContent="center" marginBottom="1rem">
            <img src={dragHandle} className="drag-handler" />
          </Box>
          <CommonGifAnimation
            width="16.875rem"
            animationData={HealthSuccessGif}
          />
          <Typography variant="h3" textAlign="center">
            Details updated successfully
          </Typography>
          <Typography variant="subtitle2" marginTop=".75rem" textAlign="center">
            To keep your account secure,
            <br /> we need you to log in again.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            className="continue-btn"
            sx={{ marginTop: "1.875rem" }}
            onClick={logoutEvent}
          >
            Log In Now
          </Button>
        </Box>
      ) : (
        <Box
          width={popupWidth + "px"}
          display="flex"
          flexDirection="column"
          gap="2rem"
          padding="2rem 1rem"
        >
          <Typography
            variant="h5"
            textAlign="center"
            textTransform="uppercase"
            letterSpacing="1.5px"
          >
            Edit {fieldNameMapping[fieldName]}
          </Typography>
          {fieldName === "dob" ? (
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              localeText={{
                ...usLocale,
                fieldMonthPlaceholder: (params) =>
                  params.contentType === "letter" ? "MMM" : "MM",
              }}
            >
              <DesktopDatePicker
                label="DOB"
                format="DD MMM YYYY"
                className="date-picker"
                value={
                  profileData[fieldName] ? dayjs(profileData[fieldName]) : null
                }
                onError={(error) =>
                  error
                    ? setHelperText("Please enter valid DOB")
                    : setHelperText("")
                }
                onChange={handleDOBchange}
                disableFuture
                views={["year", "month", "day"]}
                slotProps={{
                  textField: {
                    autoComplete: "new-password",
                    helperText:
                      (helperText?.length || 0) > 0 ? (
                        <span style={{ display: "flex", alignItems: "center" }}>
                          <InputAdornment position="start">
                            {helperText && <img src={AlertRhombus} />}
                          </InputAdornment>
                          {helperText}
                        </span>
                      ) : null,
                  },
                }}
              />
            </LocalizationProvider>
          ) : (
            <TextField
              fullWidth
              onBlur={(e) => {
                if (isPanField) {
                  if (!panRegex.test(e.target.value?.toUpperCase())) {
                    setHelperText("Please enter a valid PAN.");
                  }
                } else {
                  if (!isValidateEmail(e.target.value)) {
                    setHelperText(
                      "Email is not valid. Please enter a valid email"
                    );
                  }
                }
              }}
              label={fieldNameMapping[fieldName]}
              value={profileData[fieldName]}
              name={fieldName}
              onChange={handleOnchange}
              error={helperText.length > 0}
              helperText={
                <span style={{ display: "flex", alignItems: "center" }}>
                  <InputAdornment position="start">
                    {helperText.length > 0 && <img src={AlertRhombus} />}
                  </InputAdornment>
                  {helperText}
                </span>
              }
              inputProps={{
                maxLength: isPanField ? 10 : 200,
                inputMode: "text",
                pattern: isPanField ? panRegex : emailRegex,
                style: {
                  textTransform: isPanField ? "uppercase" : "none",
                },
              }}
            />
          )}
          {isLoading ? (
            <CustomThreeDotsLoader />
          ) : (
            <Button
              fullWidth
              variant="contained"
              disabled={
                userDetails[fieldName] === profileData[fieldName] ||
                !profileData[fieldName] ||
                !!helperText
              }
              className="continue-btn"
              onClick={handleSave}
            >
              Save
            </Button>
          )}
        </Box>
      )}
    </CustomPopup>
  );
};
export default EditProfilePopup;
